import { Grid, Button } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../components/table/FilterTable";
import DefaultPageWrapper from "../../components/wrapper/DefaultPageWrapper";
import LoaderWrapper from "../../components/wrapper/LoaderWrapper";
import { USER_ROLES } from "../../helpers/constants";
import { useAccessControl, useHasRole } from "../../hooks/auth/useCheckPermission";
import usePaginationWithSearchParams from "../../hooks/usePaginationWithSearchParams";
import { useTransfersIndividualBills } from "../../services/bankingService";
import TableService from "../../services/tableService";

import TransferNavigationBar from "../FinancialPanelTransferInvestmentBillPage/TransferNavigationBar";

import { useTransferTableFieldsConfig, TRANSFER_TABLE_NAME, TRANSFER_WITHDRAWAL_TABLE_NAME } from "./TansferIndividualBillTableFieldsConfig";

import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { centerVericalAlignIconStyle } from "../../helpers/styles";
import { useDialogBetter } from "../../hooks/useDialogBetter";
import { TransferImportListDialog } from "./ImportTransferListDialog";

import { InvestmentFilter } from "../../components/common/InvestmentFilter";
import { DateRangeFilter } from "../../components/common/DateRangeFilter/DateRangeFilter";

import UniversalToolBarWithDialogs from "../../components/bar/UniversalToolBarWithDialogs";
import {Typography} from "@mui/material";
import { formatCurrency } from "../../components/common/PriceDisplay";

import { ChangeTransferIsBookedButton } from "./ChangeTransferInBookedButton";

export default function FinancialPanelTransferIndividualBillPage(props) {
    const { hasAccess } = useAccessControl([USER_ROLES.INVESTMENT_DPI, USER_ROLES.ADMIN, USER_ROLES.ACCOUNT]);
    const hasAccessToBookTransfer = useHasRole([USER_ROLES.ACCOUNT, USER_ROLES.ADMIN]);
  
    if (!hasAccess){
        return null
    }

    const { pageName, isWithdrawalPage } = props;

    const { t } = useTranslation();
    const TABLE_FIELDS_CONFIG = useTransferTableFieldsConfig(isWithdrawalPage).filter(config=>config.name!=='has_invoice');
    const TABLE_NAME = isWithdrawalPage ? TRANSFER_WITHDRAWAL_TABLE_NAME : TRANSFER_TABLE_NAME
    
    const TABLE_CONFIGS = [{ name: TABLE_NAME, config: TABLE_FIELDS_CONFIG }];

    const [dataLocal, setDataLocal] = useState([]);
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [totalTransferAmount, setTotalTransferAmount] = useState()
    const [checkedAll, setCheckedAll] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        setSearchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const transferSearchParams = useMemo(() => {
        const tempSearchParams = new URLSearchParams(searchParams)
        tempSearchParams.set("transfer_is_withdrawal", isWithdrawalPage)
        return tempSearchParams
    }, [searchParams])



    const { data: transferData, refetch, isLoading } = useTransfersIndividualBills(transferSearchParams);
    useEffect(() => {
        if (transferData?.results) {
            setDataLocal(transferData.results);
            setRecordsCount(transferData.count);
            setTotalTransferAmount(transferData.transfer_amount_total)
        }
    }, [transferData, dataPage, dataPageSize]);

    const onClickButton = useCallback((e, id) => {
        e.stopPropagation();
    }, []);

    const trnasferDataExportHeaders = useMemo(() => {
        return TableService.getTableExportHeaders(
            TABLE_FIELDS_CONFIG, // @ts-ignore
            hiddenColumnsForTables?.[TABLE_NAME] || [], // @ts-ignore
            columnsOrdersForTables?.[TABLE_NAME] || [],
        );
    }, [TABLE_FIELDS_CONFIG, hiddenColumnsForTables, columnsOrdersForTables]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                TABLE_FIELDS_CONFIG,
                dataLocal, // @ts-ignore
                hiddenColumnsForTables?.[TABLE_NAME]
                    ? // @ts-ignore
                      hiddenColumnsForTables?.[TABLE_NAME]
                    : [], // @ts-ignore
                columnsOrdersForTables?.[TABLE_NAME]
                    ? // @ts-ignore
                      columnsOrdersForTables?.[TABLE_NAME]
                    : [],
                onClickButton,
            ),
        [
            TABLE_FIELDS_CONFIG,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    const { openDialog: openImportTransferDialog } = useDialogBetter(); 
    const handleOpenImportTransferDialog = (e) => {
        openImportTransferDialog({
            Component: TransferImportListDialog,
            componentProps: {
                isWithdrawal: isWithdrawalPage  
            }
        });
    };

    const getSeletedIds = () =>
    dataLocal
        .filter((rowData) => rowData.selected)
        .map((rowData) => rowData.id);

    const tableComponent = () => (
        <FilterTable
            // @ts-ignore
            data={data}
            countRecords={recordsCount}
            resetPageNumber={resetPageNumber}
            style={{ maxHeight: "60vh" }}
            headersConfig={TableService.orderColumns(
                TableService.hideHiddenColumns(
                    TABLE_FIELDS_CONFIG, // @ts-ignore
                    hiddenColumnsForTables?.[TABLE_NAME] || [],
                ),
                // @ts-ignore
                columnsOrdersForTables?.[TABLE_NAME] || [],
            )}
            showCleanFilterIcon={true}
            showContextMenu={false}
            showExportToFileButton={true}
            exportToFileUrl={"bankings/transfers/individual_bills/export/"}
            exportToFileSearchParams={transferSearchParams}
            exportToFileHeaders={trnasferDataExportHeaders}
            exportToFileFileName={isWithdrawalPage ? 
                `${t("bankings.exportTransferWithdrawalFileName")}.xlsx` : 
                `${t("bankings.exportTransferFileName")}.xlsx`
            }
            showDetailsIcon={false}
            checkedAll={checkedAll}
            setCheckedAll={setCheckedAll}
            showCheckbox={hasAccessToBookTransfer}
            page={dataPage}
            onPageChange={handleChangeDataPage}
            rowsPerPage={dataPageSize}
            onRowsPerPageChange={handleChangeDataPageSize}
            setDataLocal={setDataLocal}
            isLoading={isLoading}
        />
    );

    return (
        // @ts-ignore
        <DefaultPageWrapper tagTitle={t("headers.transfer_register")}>
            <Grid
                item
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                rowSpacing={1}
                columnSpacing={2}
            >
                <Grid item xs={12}>
                    < TransferNavigationBar />
                </Grid>
                <Grid item xs={12} marginY={1}>
                    <UniversalToolBarWithDialogs
                        pageName={pageName}
                        tableConfig={TABLE_CONFIGS}
                        refreshTable={refetch}
                        resetPageNumber={resetPageNumber}
                        hiddenColumnsForTables={hiddenColumnsForTables}
                        setHiddenColumnsForTables={setHiddenColumnsForTables}
                        columnsOrdersForTables={columnsOrdersForTables}
                        setColumnsOrdersForTables={setColumnsOrdersForTables}
                        showMassActionButton={false}
                        showOpenCloseFilterButton={false}
                        showCleanfiltersButton={false}
                        showMyFiltersButton={false}
                        showSearchButton={false}
                        style={{ marginBlock: "5px" }}
                        extraButtonList={[
                            <InvestmentFilter
                                filterName={"transfer_invest"}
                                searchParams={transferSearchParams}
                                setSearchParams={setSearchParams}
                                InputLabelStyle={{marginTop: "-6px"}}
                                maxHeight={"40px"}
                            />,
                                <DateRangeFilter
                                labelFrom={t("bankings.transfer_date_from")}
                                labelTo={t("bankings.transfer_date_to")}
                                filterName={"transfer_date"}
                                searchParams={transferSearchParams}
                                setSearchParams={setSearchParams}
                            />,
                            <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                startIcon={<FileUploadOutlinedIcon sx={centerVericalAlignIconStyle} />}
                                onClick={handleOpenImportTransferDialog}
                                sx={{bgcolor: "primary.light"}}
                            >
                                {isWithdrawalPage ? t("bankings.importTransferWithdrawal") : t("bankings.importTransfer")}
                            </Button>,
                            hasAccessToBookTransfer ? <ChangeTransferIsBookedButton selectedTransfers={getSeletedIds()}/> : null
                        ]}
                        extraButtonListMdGrid={[2,3, 2, hasAccessToBookTransfer ? 2 : 0]}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={false}>
                        {tableComponent()}
                        {totalTransferAmount !== undefined && <Typography
                            color={"var(--primary)"}
                            style={{
                                position: "absolute",
                                marginTop: "-40px",
                                marginLeft: "700px",
                                fontWeight: "bold",
                                fontSize: "18px",
                            }}
                        >
                            {isWithdrawalPage ? t("bankings.totalTransferWitdrawalAmount") : t("bankings.totalTransferAmount")}
                            {": "}
                            {formatCurrency(totalTransferAmount)}
                        </Typography>}
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </DefaultPageWrapper>
    );
}
